import { v4 as uuid } from 'uuid';

import {
  GetProductOrderLineItemCommand as ProductOrderLineDto,
  NewOrderModelRequest as PlaceOrderDto,
  OrderModel as ConfirmedOrderDto,
  OrderTracking as OrderDto,
  InternalOrderTracking as InternalOrderOldDto,
  OrderLineItemModel as OrderLineOldDto,
  ShippingCostRequest as CatalogShippingCostRequestDto,
  ShippingCostResponse as CatalogShippingCostResponseDto,
  ShippingCostRequestCountryCodeEnum,
  WarehouseShippingFeeRequest as WarehouseShippingCostRequestDto,
  WarehouseShippingFeeResponse as WarehouseShippingCostResponseDto,
  ProductTypeName,
  ShippingMethod,
  TransactionSourceType,
  CardPaymentMethod,
  Invoice as InvoiceDto,
  OrderLineItemPatch as UpdateOrderLineArgsDto,
  OrderAddressesPatch as UpdateOrderAddressDto,
  QuoteResponse as QuoteDto,
  NewQuoteRequest as CreateQuoteDto,
  AcceptQuoteCommand as AcceptQuoteDto,
} from '@electricjs/core_entity-client';

import { ApiListResponseDto } from '@externalTypes/general';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import { PaymentType } from '@/redux/slices/billingApiSlice';
import { PaginatedResponse } from '@/types/common';
import {
  GetOrdersArgs,
  GetInternalOrdersArgs,
  PlaceOrderArgs,
  CatalogShippingCostArgs,
  WarehouseShippingCostArgs,
  ConfirmedOrder,
  Order,
  InternalOrder,
  OrderLine,
  Invoice,
  UpdateOrderLineArgs,
  UpdateOrderAddressArgs,
  GetQuoteArgs,
  Quote,
  CanadianHardwareRequestResponse,
  CreateQuoteArgs,
  AcceptQuoteArgs,
  RejectQuoteArgs,
  CancelQuoteArgs,
} from '@/types/orders';
import { AssetCartEntry, CatalogCartEntry } from '@/types/storefront';

import { emailSeparator } from '@/components/Storefront/components/StorefrontCheckoutFormProvider';
import { CardBrand } from '@stripe/stripe-js';
import { toNumber } from '@/helpers/toNumber';

// TODO: Remove once the back-end is updated with the new properties
type PlaceOrderDtoExtended = PlaceOrderDto & {
  windows_autopilot_enrollment?: {
    microsoft_tenant_id: string;
    microsoft_group_tag_name?: string;
  };
};

// TODO: Remove once the back-end is updated with the new properties
type OrderLineDto = OrderLineOldDto & {
  tax?: number;
  refund_status?: string;
  vendor_sku?: string;
};

// TODO: Remove once the back-end is updated with the new properties
export type InternalOrderDto = InternalOrderOldDto & {
  available_to_refund?: number;
  previously_refunded?: number;
  tax_cost?: number;
  invoice_subtotal?: number;
  line_items?: OrderLineDto[];
  order_refund_status?: string;
};

const transformOrderLine = (orderLineDto: OrderLineDto): OrderLine => {
  return {
    id: orderLineDto.id,
    status: orderLineDto.status,
    chargedPrice: orderLineDto.charged_price,
    tax: orderLineDto.tax,
    provisioning: orderLineDto.provisioning_type,
    productId: orderLineDto.product_id ?? '',
    productName: orderLineDto.product_name ?? '',
    productTypeName: orderLineDto.product_type_name ?? ProductTypeName.Laptop,
    vendorSku: orderLineDto.vendor_sku,
    serialNumber: orderLineDto.serial_number,
    trackingNumber: orderLineDto.tracking_number,
    refundStatus: orderLineDto.refund_status,
    shippingMethod: orderLineDto.ship_method as ShippingMethod,
    assetId: orderLineDto.asset_id,
    shippedAssetId: orderLineDto.shipped_asset_id,
    currency: orderLineDto.currency_code,
  };
};

const sortInvoices = (
  invoices: Array<{
    dueDate?: string;
    createdAt?: string;
    paidAt?: string;
  }>
) => {
  if (!invoices.length) return invoices;

  return [...invoices].sort((a, b) => {
    // First priority: paid invoices come first
    const aIsPaid = !!a.paidAt;
    const bIsPaid = !!b.paidAt;

    if (aIsPaid && !bIsPaid) return -1; // a is paid, b is not - a comes first
    if (!aIsPaid && bIsPaid) return 1; // a is not paid, b is paid - b comes first

    // Second priority: if both are paid or both are unpaid, sort by date
    if (aIsPaid && bIsPaid) {
      // Both are paid, sort by paidAt date (most recent first)
      return new Date(b.paidAt!).getTime() - new Date(a.paidAt!).getTime();
    } else {
      // Both are unpaid, sort by dueDate (earliest first)
      const dateA = new Date(a.dueDate || '').getTime();
      const dateB = new Date(b.dueDate || '').getTime();
      return dateA - dateB;
    }
  });
};

const transformInvoice = (invoiceDto: InvoiceDto): Invoice => {
  return {
    invoiceNumber: invoiceDto.invoice_number,
    amount: toNumber(invoiceDto.amount_total) ?? 0,
    dueDate: invoiceDto.due_date,
    paidAt: invoiceDto.paid_at,
    status: invoiceDto.status,
    createdAt: invoiceDto.created_at,
    invoicePage: invoiceDto.hosted_invoice_url,
    paymentMethod: invoiceDto.payment_method
      ? {
          brand:
            ((invoiceDto.payment_method as CardPaymentMethod)
              ?.brand as CardBrand) ?? '',
          expirationMonth:
            (invoiceDto.payment_method as CardPaymentMethod)
              ?.expiration_month ?? 0,
          expirationYear:
            (invoiceDto.payment_method as CardPaymentMethod)?.expiration_year ??
            0,
          lastFourDigits: invoiceDto.payment_method?.last_four_digits ?? '',
        }
      : undefined,
  };
};

const transformOrder = (orderDto: OrderDto): Order => {
  const futureInvoices = orderDto.future_invoices?.map(transformInvoice) ?? [];
  const invoice = orderDto.invoice
    ? transformInvoice(orderDto.invoice)
    : {
        invoiceNumber: '',
        amount: 0,
        status: undefined,
        createdAt: '',
        dueDate: orderDto.created_at,
        paidAt: undefined,
        invoicePage: orderDto.invoice_page_url,
      };

  return {
    id: orderDto.id,
    friendlyId: orderDto.friendly_id,
    createdAt: orderDto.created_at ?? '',
    lines:
      orderDto.line_items?.map(line =>
        transformOrderLine(line as OrderLineDto)
      ) ?? [],
    invoiceTotal: orderDto.invoice_total,
    invoicePage: orderDto.invoice_page_url,
    invoiceURL: orderDto.invoice_pdf_url,
    status: orderDto.status,
    orderOrigin: orderDto.order_origin,
    forEmployeeId: orderDto.for_employee_id,
    forEmployeeName: orderDto.for_employee_full_name,
    requesterEmployeeId: orderDto.requester_employee_id,
    requesterEmployeeName: orderDto.requester_employee_full_name,
    recipientName: orderDto.recipient_name,
    recipientPhoneNumber: orderDto.recipient_phone_number,
    emailsForNotifications: orderDto.emails_for_notifications,
    paymentType: orderDto.is_paid_over_time
      ? PaymentType.InInstallments
      : PaymentType.InFull,
    futureInvoices: sortInvoices(futureInvoices),
    invoice: invoice,
    shippingAddress: {
      streetAddress1: orderDto.ship_to_address?.street_1 ?? '',
      streetAddress2: orderDto.ship_to_address?.street_2,
      city: orderDto.ship_to_address?.city ?? '',
      state: orderDto.ship_to_address?.state ?? '',
      country: orderDto.ship_to_address?.country ?? '',
      zip: orderDto.ship_to_address?.zip_code ?? '',
    },
    shippingNotes: orderDto.delivery_instructions,
    currency: orderDto.currency_code,
  };
};

const transformInternalOrder = (orderDto: InternalOrderDto): InternalOrder => {
  const futureInvoices = orderDto.future_invoices?.map(transformInvoice) ?? [];
  const invoice = orderDto.invoice
    ? transformInvoice(orderDto.invoice)
    : {
        invoiceNumber: '',
        amount: 0,
        status: undefined,
        createdAt: '',
        dueDate: orderDto.created_at,
        paidAt: undefined,
        invoicePage: orderDto.invoice_page_url,
      };

  return {
    id: orderDto.id,
    friendlyId: orderDto.friendly_id,
    createdAt: orderDto.created_at ?? '',
    lines:
      orderDto.line_items?.map((line: OrderLineDto) =>
        transformOrderLine(line as OrderLineDto)
      ) ?? [],
    invoiceTotal: orderDto.invoice_total,
    invoiceSubtotal: orderDto.invoice_subtotal,
    invoicePage: orderDto.invoice_page_url,
    invoiceURL: orderDto.invoice_pdf_url,
    status: orderDto.status,
    orderOrigin: orderDto.order_origin,
    forEmployeeName: orderDto.for_employee_full_name,
    recipientName: orderDto.recipient_name,
    recipientPhoneNumber: orderDto.recipient_phone_number,
    emailsForNotifications: orderDto.emails_for_notifications,
    organizationName: orderDto.organization_name,
    requesterName: orderDto.requester_employee_full_name,
    requesterEmail: orderDto.requester_employee_email,
    paymentType: orderDto.is_paid_over_time
      ? PaymentType.InInstallments
      : PaymentType.InFull,
    futureInvoices: sortInvoices(futureInvoices),
    invoice: invoice,
    shippingAddress: {
      streetAddress1: orderDto.ship_to_address?.street_1 ?? '',
      streetAddress2: orderDto.ship_to_address?.street_2,
      city: orderDto.ship_to_address?.city ?? '',
      state: orderDto.ship_to_address?.state ?? '',
      country: orderDto.ship_to_address?.country ?? '',
      zip: orderDto.ship_to_address?.zip_code ?? '',
    },
    totalTax: orderDto.tax_cost,
    billingEmail: orderDto.billing_info?.billing_email,
    billingAddress: {
      streetAddress1: orderDto.billing_info?.billing_address?.line1 ?? '',
      streetAddress2: orderDto.billing_info?.billing_address?.line2,
      city: orderDto.billing_info?.billing_address?.city ?? '',
      state: orderDto.billing_info?.billing_address?.region ?? '',
      country: orderDto.billing_info?.billing_address?.country_code ?? '',
      zip: orderDto.billing_info?.billing_address?.postal_code ?? '',
    },
    shippingNotes: orderDto.delivery_instructions,
    catalogShippingCost: orderDto.shipping_charge,
    amountAvailableToRefund: orderDto.available_to_refund,
    amountPreviouslyRefunded: orderDto.previously_refunded,
    orderRefundStatus: orderDto.order_refund_status,
    currency: orderDto.currency_code,
  };
};

const transformQuote = (quoteDto: QuoteDto): Quote => ({
  id: quoteDto.id,
  friendlyId: quoteDto.friendly_id,
  createdAt: quoteDto.created_at ?? '',
  status: quoteDto.status,
  forEmployee: {
    id: quoteDto.for_employee.id,
    name: quoteDto.for_employee.name,
  },
  requesterEmployee: {
    id: quoteDto.requester_employee.id,
    name: quoteDto.requester_employee.name,
  },
  orderFriendlyId: quoteDto.order?.friendly_id,
  quoteVendor: quoteDto.quote_vendor,
  shipToAddress: quoteDto.ship_to_address
    ? {
        streetAddress1: quoteDto.ship_to_address.street_1 ?? '',
        streetAddress2: quoteDto.ship_to_address.street_2,
        city: quoteDto.ship_to_address.city ?? '',
        state: quoteDto.ship_to_address.state ?? '',
        country: quoteDto.ship_to_address.country ?? '',
        zip: quoteDto.ship_to_address.zip_code ?? '',
      }
    : undefined,
  minLeadTime: quoteDto.min_lead_time,
  maxLeadTime: quoteDto.max_lead_time,
  quotedAt: quoteDto.quoted_at,
  expiresAt: quoteDto.expires_at,
  recipientEmail: quoteDto.recipient_email,
  recipientPhoneNumber: quoteDto.recipient_phone_number,
  quoteLineItems: quoteDto.quote_line_items
    ?.filter(lineItem => !lineItem.original_quote_line_item_id) // get only the original ones (that does not relate to any other original line item)
    ?.map(originalLineItem => ({
      id: originalLineItem.id,
      productId: originalLineItem.product_id,
      productName: originalLineItem.product?.name ?? '',
      unitEstimatedPrice: originalLineItem.unit_estimated_price ?? undefined, // converting "null" to "undefined"
      unitPrice: originalLineItem.unit_price ?? undefined,
      alternativeLineItems: quoteDto.quote_line_items
        .filter(
          lineItem =>
            lineItem.is_available &&
            lineItem.unit_price !== undefined &&
            lineItem.unit_price !== null &&
            lineItem.original_quote_line_item_id === originalLineItem.id
        )
        .map(alternativeLineItem => ({
          id: alternativeLineItem.id,
          productId: alternativeLineItem.product_id,
          productName: alternativeLineItem.product?.name ?? '',
          unitPrice: alternativeLineItem.unit_price ?? 0, // just for typescript (we already filter out all line items with null/undefined prices)
        })),
    })),
  appleResellerIds: quoteDto.resellers?.map(
    reseller => reseller.abm_reseller_id
  ),
  canceledAt: quoteDto.cancelled_at,
  canceledBy: quoteDto.canceled_by_employee?.name,
  cancellationReason: quoteDto.reason_for_cancellation,
  rejectedAt: quoteDto.rejected_at,
  rejectedBy: quoteDto.rejected_by_employee?.name,
  rejectionReason: quoteDto.reason_for_rejection,
});

const prepareAssetsOrder = (assetCart: AssetCartEntry[]): string[] => {
  return assetCart.map(entry => entry.item.id);
};

const prepareProductsOrder = (
  productCart: CatalogCartEntry[],
  paymentType: PaymentType
): ProductOrderLineDto[] => {
  const orderLines: ProductOrderLineDto[] = [];

  productCart.forEach(entry => {
    // For each cart entry, generate 1 or more order lines (based on quantity)
    for (let i = 1; i <= entry.quantity; i++) {
      const productOrderLineId = uuid();

      orderLines.push({
        line_item_id: productOrderLineId,
        product_id: entry.item.id,
        unit_price: entry.item.price ?? 0,
        provisioning_type: 'NO_PROVISIONING',
        is_paid_over_time: paymentType === PaymentType.InInstallments,
      });

      // Add the extended warranty as order line when it exists
      if (
        entry.selectedWarranty?.id &&
        entry.selectedWarranty.id !==
          entry.item.availableWarranties?.defaultWarrantyId
      ) {
        orderLines.push({
          line_item_id: uuid(),
          product_id: entry.selectedWarranty.id,
          unit_price: entry.selectedWarranty.price ?? 0,
          provisioning_type: 'NO_PROVISIONING',
          is_paid_over_time: paymentType === PaymentType.InInstallments,
          related_line_item_id: productOrderLineId, // relates the warranty to the product
        });
      }
    }
  });

  return orderLines;
};

export const orderApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getOrders: builder.query<PaginatedResponse<Order>, GetOrdersArgs>({
      query: ({ organizationId, limit, offset, orderBy, sortOrder }) => {
        return {
          url: `/v2/organizations/${organizationId}/procurements`,
          params: {
            limit: limit ?? 1000,
            offset: offset ?? 0,
            order_by: orderBy || undefined,
            sort_order: sortOrder || undefined,
          },
        };
      },
      transformResponse: (response: ApiListResponseDto<OrderDto>) => {
        const orders =
          response.results.map(order => transformOrder(order)) ?? [];

        return {
          results: orders,
          total: response.total ?? 0,
        };
      },
      providesTags: [TagTypes.Order],
    }),
    getOrderByFriendlyId: builder.query<
      Order,
      { organizationId: string; friendlyId: string }
    >({
      query: ({ organizationId, friendlyId }) => {
        return {
          url: `/v2/organizations/${organizationId}/procurements/${friendlyId}`,
        };
      },
      transformResponse: transformOrder,
      providesTags: [TagTypes.Order],
    }),
    getInternalOrders: builder.query<
      PaginatedResponse<InternalOrder>,
      GetInternalOrdersArgs
    >({
      query: ({
        limit,
        offset,
        orderBy,
        sortOrder,
        searchTerm,
        startDate,
        endDate,
        organizationName,
      }) => {
        return {
          url: `/v2/orders`,
          params: {
            limit: limit ?? 10,
            offset: offset ?? 0,
            order_by: orderBy || undefined, // using "||" to remove the param when it is empty
            sort_order: sortOrder || undefined,
            search_term: searchTerm || undefined,
            created_at_start: startDate || undefined,
            created_at_end: endDate || undefined,
            organization_name: organizationName || undefined,
          },
        };
      },
      transformResponse: (
        response: ApiListResponseDto<InternalOrderOldDto>
      ) => {
        const orders = response?.results?.map(order =>
          transformInternalOrder(order)
        );

        return {
          results: orders,
          total: response.total ?? 0,
        };
      },
      providesTags: [TagTypes.InternalOrder],
    }),
    getInternalOrderByFriendlyId: builder.query<InternalOrder, string>({
      query: friendlyId => {
        return {
          url: `/v2/orders/${friendlyId}`,
        };
      },
      transformResponse: (response: InternalOrderOldDto) => {
        return transformInternalOrder(response);
      },
      providesTags: [TagTypes.InternalOrder],
    }),
    placeOrder: builder.mutation<ConfirmedOrder | void, PlaceOrderArgs>({
      query: ({
        organizationId,
        orderIdempotentKey,
        selectedEmployeeId,
        orderOrigin,
        selectedAssets,
        selectedProducts,
        windowsZtpEnabled,
        windowsAutopilotEnrollment,
        shippingName,
        shippingPhone,
        shippingEmails,
        shippingAddress,
        shippingNotes,
        selectedCatalogShippingMethod,
        selectedWarehouseShippingMethod,
        agreedCatalogShippingCost,
        agreedWarehouseShippingCost,
        selectedPaymentMethodId,
        selectedPaymentType,
        selectedInstallmentsCount,
      }) => {
        const body: PlaceOrderDtoExtended = {
          id: orderIdempotentKey,
          for_employee_id: selectedEmployeeId,
          order_origin: orderOrigin,

          assets: selectedAssets
            ? prepareAssetsOrder(selectedAssets)
            : undefined,
          products:
            selectedProducts && selectedPaymentType
              ? prepareProductsOrder(selectedProducts, selectedPaymentType)
              : undefined,
          add_windows_ztp: windowsZtpEnabled,
          windows_autopilot_enrollment: windowsAutopilotEnrollment
            ? {
                microsoft_tenant_id:
                  windowsAutopilotEnrollment.microsoftTenantId,
                microsoft_group_tag_name:
                  windowsAutopilotEnrollment.microsoftGroupTagName,
              }
            : undefined,

          shipping_method: selectedCatalogShippingMethod,
          warehouse_shipping_method: selectedWarehouseShippingMethod,
          shipping_cost: agreedCatalogShippingCost,
          warehouse_shipping_cost: agreedWarehouseShippingCost,

          recipient:
            shippingName && shippingPhone
              ? { name: shippingName, phone_number: shippingPhone }
              : undefined,
          emails_for_notifications: shippingEmails
            ? shippingEmails.split(emailSeparator).map(email => email.trim())
            : undefined,
          delivery_instructions: shippingNotes,

          payment_method_id: selectedPaymentMethodId,
          transaction_source_type: selectedPaymentMethodId
            ? selectedPaymentType === PaymentType.InInstallments
              ? TransactionSourceType.PayOverTimePurchase
              : TransactionSourceType.OneOffPurchase
            : undefined,
          interval_count:
            selectedPaymentType === PaymentType.InInstallments
              ? selectedInstallmentsCount
              : undefined,
        };

        if (shippingAddress) {
          body.ship_to_address = {
            street_1: shippingAddress.streetAddress1,
            street_2: shippingAddress.streetAddress2 ?? undefined,
            city: shippingAddress.city,
            state: shippingAddress.state,
            country: shippingAddress.country,
            zip_code: shippingAddress.zip,
          };

          body.final_destination_address = {
            street_1: shippingAddress.streetAddress1,
            street_2: shippingAddress.streetAddress2 ?? undefined,
            city: shippingAddress.city,
            state: shippingAddress.state,
            country: shippingAddress.country,
            zip_code: shippingAddress.zip,
          };
        }

        return {
          url: `v2/organizations/${organizationId}/procurements`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response?: ConfirmedOrderDto) => {
        // only requests containing products will generate real orders
        if (response) {
          return {
            id: response.id,
            friendlyId: response.friendly_id,
            invoiceURL: response.invoice_pdf_url,
            futurePaymentsDates: response.future_payments_schedule,
          };
        }
      },
      invalidatesTags: [
        TagTypes.Order,
        TagTypes.InternalOrder,
        TagTypes.Asset, // because an asset might be requested, so the available assets list might have changed
        TagTypes.ProductSuggestions, // because this order might impact further suggestions
      ],
    }),
    calculateCatalogShippingCost: builder.mutation<
      number,
      CatalogShippingCostArgs
    >({
      query: ({
        organizationId,
        countryCode,
        selectedCatalogShippingMethod,
        selectedProducts,
      }) => {
        const body: CatalogShippingCostRequestDto = {
          shipping_method: selectedCatalogShippingMethod as ShippingMethod,
          products: selectedProducts.map(entry => ({
            product_type: entry.item.productType as ProductTypeName,
            quantity: entry.quantity,
          })),
          country_code: countryCode as ShippingCostRequestCountryCodeEnum,
        };

        return {
          url: `v2/organizations/${organizationId}/procurements/shipping_cost/calculate`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: CatalogShippingCostResponseDto) =>
        response.shipping_cost,
    }),
    calculateWarehouseShippingCost: builder.mutation<
      number,
      WarehouseShippingCostArgs
    >({
      query: ({
        organizationId,
        selectedWarehouseShippingMethod,
        warehouseAssetIds,
      }) => {
        const body: WarehouseShippingCostRequestDto = {
          warehouse_assets_ids: warehouseAssetIds,
          warehouse_shipping_method: selectedWarehouseShippingMethod,
        };

        return {
          url: `v2/organizations/${organizationId}/procurements/warehouse_shipping/calculate`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: WarehouseShippingCostResponseDto) =>
        response.total_shipping_fee,
    }),
    updateOrderLine: builder.mutation<void, UpdateOrderLineArgs>({
      query: ({ orderId, orderLineId, serialNumber, trackingNumber }) => {
        const body: UpdateOrderLineArgsDto = {
          ...(serialNumber !== undefined && { serial_number: serialNumber }),
          ...(trackingNumber !== undefined && {
            tracking_number: trackingNumber,
          }),
        };

        return {
          url: `/v2/orders/${orderId}/line_items/${orderLineId}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TagTypes.InternalOrder],
    }),
    updateShippingAddress: builder.mutation<void, UpdateOrderAddressArgs>({
      query: ({ orderId, shippingAddress }) => {
        const body: UpdateOrderAddressDto = {
          ship_to_address: {
            street_1: shippingAddress.streetAddress1,
            street_2: shippingAddress.streetAddress2 || undefined,
            city: shippingAddress.city,
            state: shippingAddress.state,
            zip_code: shippingAddress.zip,
            country: shippingAddress.country,
          },
          final_destination_address: {
            street_1: shippingAddress.streetAddress1,
            street_2: shippingAddress.streetAddress2 || undefined,
            city: shippingAddress.city,
            state: shippingAddress.state,
            zip_code: shippingAddress.zip,
            country: shippingAddress.country,
          },
        };

        return {
          url: `/v2/orders/${orderId}/addresses`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TagTypes.InternalOrder, TagTypes.Order],
    }),
    cancelOrder: builder.mutation<void, string>({
      query: friendlyId => ({
        url: `/v2/orders/${friendlyId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: [
        TagTypes.InternalOrder,
        TagTypes.Order,
        TagTypes.Asset, // because all associated assets will be deleted
      ],
    }),

    //
    // Quotes
    //
    getHardwareQuotes: builder.query<PaginatedResponse<Quote>, GetQuoteArgs>({
      query: ({ organizationId, limit, offset, orderBy, sortOrder }) => {
        return {
          url: `/v2/organizations/${organizationId}/quotes`,
          params: {
            limit,
            offset,
            order_by: orderBy || undefined,
            sort_order: sortOrder || undefined,
          },
        };
      },
      transformResponse: (response: ApiListResponseDto<QuoteDto>) => {
        const quotes = response.results.map(transformQuote) ?? [];
        return {
          results: quotes,
          total: response.total ?? 0,
        };
      },
      providesTags: [TagTypes.HardwareQuote],
    }),
    getHardwareQuoteByFriendlyId: builder.query<
      Quote,
      { organizationId: string; friendlyId: string }
    >({
      query: ({ organizationId, friendlyId }) => ({
        url: `/v2/organizations/${organizationId}/quotes/${friendlyId}`,
      }),
      transformResponse: (response: QuoteDto) => transformQuote(response),
      providesTags: [TagTypes.HardwareQuote],
    }),
    createHardwareQuote: builder.mutation<
      Quote | CanadianHardwareRequestResponse,
      CreateQuoteArgs
    >({
      query: ({
        organizationId,
        employeeId,
        recipientNationalId,
        shippingAddress,
        phoneNumber,
        email,
        productId,
        extendedWarrantyId,
        targetDeliveryDate,
      }) => {
        const quoteLineId = uuid();

        const body: CreateQuoteDto = {
          recipient: {
            national_id: recipientNationalId,
            phone_number: phoneNumber,
            email,
            for_employee_id: employeeId,
            ship_to_address: {
              street_1: shippingAddress.streetAddress1,
              street_2: shippingAddress.streetAddress2 || undefined,
              city: shippingAddress.city,
              state: shippingAddress.state,
              zip_code: shippingAddress.zip,
              country: shippingAddress.country,
            },
          },
          quote_line_items: [
            {
              quote_line_item_id: quoteLineId,
              product_id: productId, // first, we add just the product (without warranty)
            },
          ],
          target_delivery_date: targetDeliveryDate.toISOString().split('T')[0],
        };

        // handling the extended warranty
        if (extendedWarrantyId) {
          body.quote_line_items.push({
            quote_line_item_id: uuid(),
            product_id: extendedWarrantyId,
            related_quote_line_item_id: quoteLineId, // relates the warranty to the product
          });
        }

        return {
          url: `/v2/organizations/${organizationId}/quotes`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (
        response: QuoteDto | { message: string }
      ): Quote | CanadianHardwareRequestResponse => {
        if ('id' in response) {
          return transformQuote(response);
        }
        return { isManualFulfillment: true, message: response.message };
      },
      invalidatesTags: [TagTypes.HardwareQuote],
    }),
    acceptHardwareQuote: builder.mutation<string, AcceptQuoteArgs>({
      query: ({
        organizationId,
        friendlyId,
        acceptedLineItemIds,
        selectedPaymentMethodId,
        orderIdempotentKey,
        selectedPaymentType,
        selectedInstallmentsCount,
      }) => {
        const isInstallments =
          selectedPaymentType === PaymentType.InInstallments;

        const body: AcceptQuoteDto = {
          idempotency_key: orderIdempotentKey,
          quote_line_item_ids: acceptedLineItemIds,
          payment_method_id: selectedPaymentMethodId,
          transaction_source_type: isInstallments
            ? TransactionSourceType.PayOverTimePurchase
            : TransactionSourceType.OneOffPurchase,
          interval_count: isInstallments
            ? selectedInstallmentsCount
            : undefined,
        };

        return {
          url: `/v2/organizations/${organizationId}/quotes/${friendlyId}/accept`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: QuoteDto) =>
        response.order?.friendly_id ?? '',
      invalidatesTags: [
        TagTypes.HardwareQuote,
        TagTypes.Order,
        TagTypes.InternalOrder,
        TagTypes.ProductSuggestions, // because this order might impact further suggestions
      ],
    }),
    rejectHardwareQuote: builder.mutation<void, RejectQuoteArgs>({
      query: ({ organizationId, friendlyId, reason }) => ({
        url: `/v2/organizations/${organizationId}/quotes/${friendlyId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [TagTypes.HardwareQuote],
    }),
    cancelHardwareQuote: builder.mutation<void, CancelQuoteArgs>({
      query: ({ organizationId, friendlyId, reason }) => ({
        url: `/v2/organizations/${organizationId}/quotes/${friendlyId}/cancel`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [TagTypes.HardwareQuote],
    }),
  }),
});

// export auto-generated hooks
export const {
  useCancelOrderMutation,
  useGetOrdersQuery,
  useGetInternalOrdersQuery,
  useGetInternalOrderByFriendlyIdQuery,
  useGetOrderByFriendlyIdQuery,
  usePlaceOrderMutation,
  useCalculateCatalogShippingCostMutation,
  useCalculateWarehouseShippingCostMutation,
  useUpdateOrderLineMutation,
  useUpdateShippingAddressMutation,
  useGetHardwareQuotesQuery,
  useGetHardwareQuoteByFriendlyIdQuery,
  useCreateHardwareQuoteMutation,
  useAcceptHardwareQuoteMutation,
  useRejectHardwareQuoteMutation,
  useCancelHardwareQuoteMutation,
} = orderApiSlice;
